import { useEffect, useRef, useState } from "react";

// ───────────────────────────────────────────────────────────────
// Develop — media that arrives in 1-bit and develops into color.
//
// The image or video is drawn through a small WebGL pass: first as
// an ordered (8×8 Bayer) dither in two inks, then pixel by pixel the
// same matrix hands each cell over to the full-color source. Color is
// something you wait a beat for.
//
// The <video>/<img> stays mounted underneath as the texture source
// (and as the fallback when WebGL isn't there).
// ───────────────────────────────────────────────────────────────

const HOLD_MS = 160; // time spent fully 1-bit before color starts
const DEVELOP_MS = 900;

const VERT = `
attribute vec2 a_pos;
varying vec2 v_uv;
void main() {
  v_uv = vec2(a_pos.x * 0.5 + 0.5, 0.5 - a_pos.y * 0.5);
  gl_Position = vec4(a_pos, 0.0, 1.0);
}`;

const FRAG = `
precision mediump float;
varying vec2 v_uv;
uniform sampler2D u_tex;
uniform vec2 u_res;
uniform float u_cell;
uniform float u_progress;
uniform vec3 u_lo;
uniform vec3 u_hi;

float bayer2(vec2 a) { a = floor(a); return fract(a.x / 2.0 + a.y * a.y * 0.75); }
float bayer4(vec2 a) { return bayer2(0.5 * a) * 0.25 + bayer2(a); }
float bayer8(vec2 a) { return bayer4(0.5 * a) * 0.25 + bayer2(a); }

void main() {
  vec2 cell = floor(gl_FragCoord.xy / u_cell);
  // sample once per dither cell so the 1-bit layer reads as pixels
  vec2 cuv = (cell + 0.5) * u_cell / u_res;
  cuv.y = 1.0 - cuv.y;
  vec3 src = texture2D(u_tex, cuv).rgb;
  float lum = dot(src, vec3(0.299, 0.587, 0.114));
  float t = bayer8(cell);
  vec3 mono = lum > t ? u_hi : u_lo;

  // same matrix, rotated, decides the order cells turn to color
  float order = bayer8(cell.yx + 5.0);
  float shown = step(order, u_progress * 1.001);
  gl_FragColor = vec4(mix(mono, texture2D(u_tex, v_uv).rgb, shown), 1.0);
}`;

const hexToVec = (hex) =>
  hex
    .replace("#", "")
    .match(/\w\w/g)
    .map((x) => parseInt(x, 16) / 255);

const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

function compile(gl, type, src) {
  const s = gl.createShader(type);
  gl.shaderSource(s, src);
  gl.compileShader(s);
  return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null;
}

export const isVideo = (src) => /\.(mp4|webm)$/i.test(src);

// Average the media's outer border (a 32×18 thumbnail's edge pixels) —
// the color the frame would need to disappear into the page.
function edgeColor(media) {
  const w = 32;
  const h = 18;
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(media, 0, 0, w, h);
  const d = ctx.getImageData(0, 0, w, h).data;
  let r = 0;
  let g = 0;
  let b = 0;
  let n = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (x > 0 && x < w - 1 && y > 0 && y < h - 1) continue;
      const i = (y * w + x) * 4;
      r += d[i];
      g += d[i + 1];
      b += d[i + 2];
      n += 1;
    }
  }
  return `#${[r, g, b].map((v) => Math.round(v / n).toString(16).padStart(2, "0")).join("")}`;
}

export default function Develop({ src, ratio, lo = "#111111", hi = "#f4f4f1", onEdge }) {
  const onEdgeRef = useRef(onEdge);
  onEdgeRef.current = onEdge;
  const canvasRef = useRef(null);
  const mediaRef = useRef(null);
  const [gl, setGl] = useState("pending"); // pending | on | off

  useEffect(() => {
    const canvas = canvasRef.current;
    const media = mediaRef.current;
    if (!canvas || !media) return undefined;

    const ctx = canvas.getContext("webgl", { antialias: false, premultipliedAlpha: false });
    if (!ctx) {
      setGl("off");
      return undefined;
    }
    const vs = compile(ctx, ctx.VERTEX_SHADER, VERT);
    const fs = compile(ctx, ctx.FRAGMENT_SHADER, FRAG);
    const prog = ctx.createProgram();
    if (!vs || !fs) {
      setGl("off");
      return undefined;
    }
    ctx.attachShader(prog, vs);
    ctx.attachShader(prog, fs);
    ctx.linkProgram(prog);
    ctx.useProgram(prog);

    const buf = ctx.createBuffer();
    ctx.bindBuffer(ctx.ARRAY_BUFFER, buf);
    ctx.bufferData(ctx.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), ctx.STATIC_DRAW);
    const aPos = ctx.getAttribLocation(prog, "a_pos");
    ctx.enableVertexAttribArray(aPos);
    ctx.vertexAttribPointer(aPos, 2, ctx.FLOAT, false, 0, 0);

    const tex = ctx.createTexture();
    ctx.bindTexture(ctx.TEXTURE_2D, tex);
    ctx.texParameteri(ctx.TEXTURE_2D, ctx.TEXTURE_WRAP_S, ctx.CLAMP_TO_EDGE);
    ctx.texParameteri(ctx.TEXTURE_2D, ctx.TEXTURE_WRAP_T, ctx.CLAMP_TO_EDGE);
    ctx.texParameteri(ctx.TEXTURE_2D, ctx.TEXTURE_MIN_FILTER, ctx.LINEAR);
    ctx.texParameteri(ctx.TEXTURE_2D, ctx.TEXTURE_MAG_FILTER, ctx.LINEAR);

    const u = (n) => ctx.getUniformLocation(prog, n);
    const uRes = u("u_res");
    const uCell = u("u_cell");
    const uProgress = u("u_progress");
    ctx.uniform3fv(u("u_lo"), hexToVec(lo));
    ctx.uniform3fv(u("u_hi"), hexToVec(hi));

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      canvas.width = Math.max(1, Math.round(canvas.clientWidth * dpr));
      canvas.height = Math.max(1, Math.round(canvas.clientHeight * dpr));
      ctx.viewport(0, 0, canvas.width, canvas.height);
      ctx.uniform2f(uRes, canvas.width, canvas.height);
      ctx.uniform1f(uCell, Math.round(2 * dpr));
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let start = null; // clock starts on the first real frame, not on mount
    let raf;
    const ready = () =>
      isVideo(src) ? media.readyState >= 2 : media.complete && media.naturalWidth > 0;

    const frame = (now) => {
      if (ready()) {
        try {
          ctx.texImage2D(ctx.TEXTURE_2D, 0, ctx.RGB, ctx.RGB, ctx.UNSIGNED_BYTE, media);
          if (start === null) {
            start = now;
            setGl("on");
            try {
              if (onEdgeRef.current) onEdgeRef.current(edgeColor(media));
            } catch (e) {
              /* unreadable source — the page just keeps its color */
            }
          }
          const p = reduce ? 1 : Math.min(1, Math.max(0, (now - start - HOLD_MS) / DEVELOP_MS));
          ctx.uniform1f(uProgress, easeInOut(p));
          ctx.drawArrays(ctx.TRIANGLE_STRIP, 0, 4);
        } catch (e) {
          setGl("off"); // tainted or unreadable source — show it plain
          return;
        }
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      ctx.deleteTexture(tex);
      ctx.deleteBuffer(buf);
      ctx.deleteProgram(prog);
    };
  }, [src, lo, hi]);

  const fill = {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  };

  return (
    <div className="develop" style={{ aspectRatio: ratio || 16 / 9 }}>
      {isVideo(src) ? (
        <video ref={mediaRef} src={src} autoPlay loop muted playsInline style={{ ...fill, opacity: gl === "off" ? 1 : 0 }} />
      ) : (
        <img ref={mediaRef} src={src} alt="" style={{ ...fill, opacity: gl === "off" ? 1 : 0 }} />
      )}
      <canvas ref={canvasRef} aria-hidden style={{ ...fill, opacity: gl === "on" ? 1 : 0 }} />
    </div>
  );
}
